import type { AxiosInstance } from 'axios';

import type { OrderStatus } from '@accio3d/shared/domain';
import type { FunctionalRequirements } from '@accio3d/shared-ui/printability/services/printability3dService';
import type {
  Storefront,
  StorefrontLocation,
  StorefrontCompanyProfile,
  StorefrontCapabilitySelection,
  StorefrontScore,
} from '@accio3d/shared-ui/types/storefront';

/** Add a location — `id` is server-generated, so it is omitted from the request. */
export type AddStorefrontLocationRequest = Omit<StorefrontLocation, 'id'>;

/**
 * One part on a supplier's order, mirroring the API's `OrderPartResponse`.
 * `documents` carries that part's signed, downloadable files.
 */
export interface SupplierOrderPart {
  manufacturingRequirementId: string;
  partId: string;
  partName: string | null;
  partNumber: string | null;
  material: string | null;
  processType: string | null;
  color: string | null;
  infill: number | null;
  postProcesses: string[];
  quantity: number;
  partLengthMm: number | null;
  partWidthMm: number | null;
  partHeightMm: number | null;
  partMassG: number | null;
  tightestToleranceMm: number | null;
  notes: string | null;
  pricePerUnit: number | null;
  subTotal: number | null;
  /** Parts only — no shipping or tax, see SupplierOrder.total. */
  total: number | null;
  documents: OrderDocument[];
  previewUrl: string | null;
  thumbnailUrl: string | null;
  /** Snapshot from the RFQ; null when the backend hasn't loaded that relation. */
  functionalRequirements: FunctionalRequirements | null;
}

/**
 * A single order as returned by `GET /suppliers/orders/:id`, matching the
 * API's `toOrderResponse` shape. Fields the backend cannot yet supply
 * (tolerances, PO terms) are absent and rendered as em dashes by the
 * order-detail page.
 */
export interface SupplierOrder {
  id: string;
  rfqId: string;
  quoteId: string;
  rfqNumber: string;
  orderNumber: string;
  organizationId: string;
  purchaserId: string;
  purchaserName: string;
  /** Purchaser org name; null on paths that don't load the organization relation. */
  customerName: string | null;
  parts: SupplierOrderPart[];
  dueDate: string | null;
  paidAt: string | null;
  status: OrderStatus;
  paymentStatus: string;
  shippingLabel: string | null;
  isReconstruction: boolean;
  deliverableUrl: string | null;
  shippingFrom: string;
  shippingTo: string;
  subTotal: number;
  tax: number | null;
  total: number;
  supplierId: string;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
  selectedShipmentId: string | null;
  selectedRateId: string | null;
}

/** A downloadable part document (3D model, drawing, spec, data) with a signed URL. */
export interface OrderDocument {
  name: string;
  url: string;
  kind: string;
}

/** Partial update of a single location (addressed by id in the URL). */
export type UpdateStorefrontLocationRequest = Partial<Omit<StorefrontLocation, 'id'>>;

interface UpdateStorefrontProfileRequest {
  profile: StorefrontCompanyProfile;
}

interface UpdateStorefrontIndustriesRequest {
  industries: string[];
}

interface UpdateStorefrontCapabilitiesRequest {
  capabilities: StorefrontCapabilitySelection[];
}

interface UpdateStorefrontScoresRequest {
  scores: StorefrontScore[];
}

interface UpdateStorefrontRegionsRequest {
  shippingRegions: string[];
}

interface UpdateStorefrontVisibilityRequest {
  isPublished: boolean;
}

interface UpdateStorefrontSlugRequest {
  slug?: string;
}

interface UpdateStorefrontMinimumOrderRequest {
  minimumOrderAmount: number | null;
}

export interface StorefrontMinimumOrder {
  minimumOrderAmount: number | null;
}

interface FetchAndFillResponse {
  name?: string;
  tagline?: string;
  description?: string;
  services: string[];
  certifications: string[];
  materials: string[];
  technologies: string[];
  industries: string[];
  location?: string;
  websiteUrl: string;
}

export interface CatalogueEquipment {
  id: number;
  name: string;
  manufacturer: string;
  equipmentTypeId: number;
  equipmentTypeName: string;
}

export interface MaterialReference {
  id: number;
  name: string;
}

export interface TechnologyReference {
  id: number;
  name: string;
}

export function createSupplierService(client: AxiosInstance) {
  return {
    async getStorefront(supplierId: string): Promise<Storefront> {
      const { data } = await client.get<Storefront>(`/suppliers/${supplierId}/storefront`);
      return data;
    },

    async addStorefrontLocation(
      supplierId: string,
      body: AddStorefrontLocationRequest,
    ): Promise<Storefront> {
      const { data } = await client.post<Storefront>(
        `/suppliers/${supplierId}/storefront/locations`,
        body,
      );
      return data;
    },

    async updateStorefrontLocation(
      supplierId: string,
      locationId: string,
      body: UpdateStorefrontLocationRequest,
    ): Promise<Storefront> {
      const { data } = await client.patch<Storefront>(
        `/suppliers/${supplierId}/storefront/locations/${locationId}`,
        body,
      );
      return data;
    },

    async deleteStorefrontLocation(supplierId: string, locationId: string): Promise<void> {
      await client.delete(`/suppliers/${supplierId}/storefront/locations/${locationId}`);
    },

    async updateStorefrontProfile(
      supplierId: string,
      body: UpdateStorefrontProfileRequest,
    ): Promise<Storefront> {
      const { data } = await client.put<Storefront>(
        `/suppliers/${supplierId}/storefront/profile`,
        body,
      );
      return data;
    },

    async updateStorefrontIndustries(
      supplierId: string,
      body: UpdateStorefrontIndustriesRequest,
    ): Promise<Storefront> {
      const { data } = await client.put<Storefront>(
        `/suppliers/${supplierId}/storefront/industries`,
        body,
      );
      return data;
    },

    async updateStorefrontCapabilities(
      supplierId: string,
      body: UpdateStorefrontCapabilitiesRequest,
    ): Promise<Storefront> {
      const { data } = await client.put<Storefront>(
        `/suppliers/${supplierId}/storefront/capabilities`,
        body,
      );
      return data;
    },

    async updateStorefrontScores(
      supplierId: string,
      body: UpdateStorefrontScoresRequest,
    ): Promise<Storefront> {
      const { data } = await client.put<Storefront>(
        `/suppliers/${supplierId}/storefront/scores`,
        body,
      );
      return data;
    },

    async updateStorefrontRegions(
      supplierId: string,
      body: UpdateStorefrontRegionsRequest,
    ): Promise<Storefront> {
      const { data } = await client.put<Storefront>(
        `/suppliers/${supplierId}/storefront/regions`,
        body,
      );
      return data;
    },

    async updateStorefrontVisibility(
      supplierId: string,
      body: UpdateStorefrontVisibilityRequest,
    ): Promise<Storefront> {
      const { data } = await client.put<Storefront>(
        `/suppliers/${supplierId}/storefront/visibility`,
        body,
      );
      return data;
    },

    async updateStorefrontSlug(
      supplierId: string,
      body: UpdateStorefrontSlugRequest,
    ): Promise<Storefront> {
      const { data } = await client.put<Storefront>(
        `/suppliers/${supplierId}/storefront/slug`,
        body,
      );
      return data;
    },

    async getStorefrontMinimumOrder(supplierId: string): Promise<StorefrontMinimumOrder> {
      const { data } = await client.get<StorefrontMinimumOrder>(
        `/suppliers/${supplierId}/storefront/minimum-order`,
      );
      return data;
    },

    async updateStorefrontMinimumOrder(
      supplierId: string,
      body: UpdateStorefrontMinimumOrderRequest,
    ): Promise<Storefront> {
      const { data } = await client.put<Storefront>(
        `/suppliers/${supplierId}/storefront/minimum-order`,
        body,
      );
      return data;
    },

    async getStorefrontGalleryUploadUrls(
      supplierId: string,
      files: { fileName: string; contentType: string }[],
    ): Promise<{
      uploads: { fileName: string; uploadUrl: string; blobUrl: string; expiresAt: string }[];
    }> {
      const { data } = await client.post<{
        uploads: { fileName: string; uploadUrl: string; blobUrl: string; expiresAt: string }[];
      }>(`/suppliers/${supplierId}/storefront/gallery/uploads`, { files });
      return data;
    },

    async addStorefrontGalleryItem(
      supplierId: string,
      body: { blobUrl: string; mimeType: string; caption?: string },
    ): Promise<Storefront> {
      const { data } = await client.post<Storefront>(
        `/suppliers/${supplierId}/storefront/gallery`,
        body,
      );
      return data;
    },

    async deleteStorefrontGalleryItem(supplierId: string, itemId: string): Promise<Storefront> {
      const { data } = await client.delete<Storefront>(
        `/suppliers/${supplierId}/storefront/gallery/${itemId}`,
      );
      return data;
    },

    async fetchAndFill(websiteUrl: string): Promise<FetchAndFillResponse> {
      try {
        const { data } = await client.post<FetchAndFillResponse>('/suppliers/fetch-and-fill', {
          websiteUrl,
        });
        return data;
      } catch {
        throw new Error(
          'Failed to fetch and fill storefront data. Please check the website URL and try again.',
        );
      }
    },

    async getIndustries(search?: string): Promise<{
      data: string[];
      total: number;
      page: number;
      limit: number;
    }> {
      const { data } = await client.get<{
        data: string[];
        total: number;
        page: number;
        limit: number;
      }>('/industries', { params: { search } });
      return data;
    },

    async getRegions(search?: string): Promise<{
      data: { value: string; label: string }[];
      total: number;
      page: number;
      limit: number;
    }> {
      const { data } = await client.get<{
        data: { value: string; label: string }[];
        total: number;
        page: number;
        limit: number;
      }>('/regions', { params: { search } });
      return data;
    },

    async submitMembershipApplication(body: {
      tier: string;
      notes?: string;
    }): Promise<SupplierMembershipApplication> {
      const { data } = await client.post<SupplierMembershipApplication>(
        '/suppliers/membership-application',
        body,
      );
      return data;
    },

    /** Fetch a single order the active supplier is fulfilling. */
    async getOrder(orderId: string): Promise<SupplierOrder> {
      const { data } = await client.get<SupplierOrder>(`/suppliers/orders/${orderId}`);
      return data;
    },

    async updateOrderStatus(
      orderId: string,
      status: OrderStatus,
      deliverableFileUrl?: string,
    ): Promise<SupplierOrder> {
      const { data } = await client.patch<SupplierOrder>(`/suppliers/orders/${orderId}/status`, {
        status,
        ...(deliverableFileUrl ? { deliverableFileUrl } : {}),
      });
      return data;
    },

    async createDeliverableUpload(
      orderId: string,
      fileName: string,
    ): Promise<{ uploadUrl: string; blobUrl: string }> {
      const { data } = await client.post<{ uploadUrl: string; blobUrl: string }>(
        `/suppliers/orders/${orderId}/deliverable-upload`,
        { fileName },
      );
      return data;
    },
  };
}

interface SupplierMembershipApplication {
  supplierId: string;
  organizationName: string;
  tier: string;
  status: 'under_review' | 'approved' | 'rejected';
  appliedAt: string | null;
  notes: string | null;
}

export type SupplierService = ReturnType<typeof createSupplierService>;
